import { useMutation, useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS, stanAnalytics, stanNotify } from '@/lib';
import { useUser } from '@/queries/useUser';
import {
    addToEmailFlow,
    bulkAddTag,
    bulkDeleteCustomers,
    bulkGrantAffiliateAccess,
    bulkGrantProductAccess,
    bulkRemoveTag,
    fetchBulkUserFanIds,
} from '@/lib/api/customers';

type BatchProcessor = <T>(
    userFanIds: number[],
    batchFn: (batch: number[]) => Promise<T>,
    batchSize?: number
) => Promise<boolean>;

interface BulkMutationOptions {
    getFilterArray: () => string;
    processBatch: BatchProcessor;
    onSuccess?: (result?: { totalDeleted?: number; totalCount?: number }) => void;
    onError?: (error: Error) => void;
    resetState: () => void;
}

function useInvalidateQueries() {
    const queryClient = useQueryClient();
    return () => {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.AUDIENCE] });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FAN_TAGS] });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CUSTOMER_DETAILS] });
    };
}

export function useFetchBulkUserFanIds(options: { onError?: (error: Error) => void }) {
    return useMutation({
        mutationFn: (filterArray: string) => fetchBulkUserFanIds(filterArray || '[]'),
        onError: (error: Error) => {
            stanNotify({
                type: 'error',
                title: 'Failed to fetch customers',
                text: error.message || 'Could not fetch customer list. Please try again.',
                duration: 5000,
            });
            options.onError?.(error);
        },
    });
}

export function useAddTagMutation(options: BulkMutationOptions) {
    const invalidateQueries = useInvalidateQueries();
    const { user } = useUser();
    const fetchUserFanIdsMutation = useFetchBulkUserFanIds({
        onError: options.onError,
    });

    return useMutation({
        mutationFn: async ({ tag }: { tag: string }) => {
            const userFanIds = await fetchUserFanIdsMutation.mutateAsync(options.getFilterArray());
            await options.processBatch(userFanIds, batch => {
                const data = { tag, user_fan_ids: batch };
                // Track analytics per batch (matching Vue behavior)
                stanAnalytics('creator-customers-tag-added', {
                    meta: { user_id: user?.user_id, username: user?.username },
                    props: data,
                });
                return bulkAddTag(data);
            });
        },
        onSuccess: () => {
            invalidateQueries();
            stanNotify({
                type: 'success',
                title: 'Success',
                text: 'The tags have been added.', // Match Vue text format
                duration: 10000, // Match Vue duration
            });
            options.resetState();
            options.onSuccess?.();
        },
        onError: (error: Error) => {
            stanNotify({
                type: 'error',
                title: 'Tags could not be created, let us help you with that!', // Match Vue error title
                text: error.message || 'Please email friends@stanwith.me', // Match Vue error text
                duration: 4000, // Match Vue error duration
            });
            options.resetState();
            options.onError?.(error);
        },
    });
}

export function useRemoveTagMutation(options: BulkMutationOptions) {
    const invalidateQueries = useInvalidateQueries();
    const { user } = useUser();
    const fetchUserFanIdsMutation = useFetchBulkUserFanIds({
        onError: options.onError,
    });

    return useMutation({
        mutationFn: async ({ tag }: { tag: string }) => {
            const userFanIds = await fetchUserFanIdsMutation.mutateAsync(options.getFilterArray());
            await options.processBatch(userFanIds, batch => {
                const data = { tag, user_fan_ids: batch };
                // Track analytics per batch (matching Vue behavior)
                stanAnalytics('creator-customers-tag-removed', {
                    meta: { user_id: user?.user_id, username: user?.username },
                    props: data,
                });
                return bulkRemoveTag(data);
            });
        },
        onSuccess: () => {
            invalidateQueries();
            stanNotify({
                type: 'success',
                title: 'Success',
                text: 'The tags have been removed.', // Match Vue text format
                duration: 10000, // Match Vue duration
            });
            options.resetState();
            options.onSuccess?.();
        },
        onError: (error: Error) => {
            stanNotify({
                type: 'error',
                title: 'Tags could not be created, let us help you with that!', // Match Vue error title
                text: error.message || 'Please email friends@stanwith.me', // Match Vue error text
                duration: 4000, // Match Vue error duration
            });
            options.resetState();
            options.onError?.(error);
        },
    });
}

export function useGrantProductMutation(options: BulkMutationOptions) {
    const invalidateQueries = useInvalidateQueries();
    const { user } = useUser();
    const fetchUserFanIdsMutation = useFetchBulkUserFanIds({
        onError: options.onError,
    });

    return useMutation({
        mutationFn: async ({ pageId }: { pageId: number }) => {
            const userFanIds = await fetchUserFanIdsMutation.mutateAsync(options.getFilterArray());
            const batchSize = Math.min(100, Math.ceil(userFanIds.length / 2));
            await options.processBatch(
                userFanIds,
                batch => {
                    const data = { page_id: pageId, user_fan_ids: batch };
                    // Track analytics per batch (matching Vue behavior)
                    stanAnalytics('creator-customers-added-to-product', {
                        meta: { user_id: user?.user_id, username: user?.username },
                        props: data,
                    });
                    return bulkGrantProductAccess(data);
                },
                batchSize
            );
        },
        onSuccess: () => {
            invalidateQueries();
            stanNotify({
                type: 'success',
                title: 'Success',
                text: 'Product access has been granted.',
                duration: 10000, // Match Vue duration
            });
            options.resetState();
            options.onSuccess?.();
        },
        onError: (error: Error) => {
            stanNotify({
                type: 'error',
                title: 'Product Access could not be granted, let us help you with that!', // Match Vue error title
                text: error.message || 'Please email friends@stanwith.me', // Match Vue error text
                duration: 4000, // Match Vue error duration
            });
            options.resetState();
            options.onError?.(error);
        },
    });
}

export function useGrantAffiliateMutation(options: BulkMutationOptions) {
    const invalidateQueries = useInvalidateQueries();
    const { user } = useUser();
    const fetchUserFanIdsMutation = useFetchBulkUserFanIds({
        onError: options.onError,
    });

    return useMutation({
        mutationFn: async ({ pageId }: { pageId: number }) => {
            const userFanIds = await fetchUserFanIdsMutation.mutateAsync(options.getFilterArray());
            const batchSize = Math.min(100, Math.ceil(userFanIds.length / 2));
            await options.processBatch(
                userFanIds,
                batch => {
                    const data = { page_id: pageId, user_fan_ids: batch };
                    // Track analytics per batch (matching Vue behavior)
                    stanAnalytics('creator-customers-added-to-affiliate', {
                        meta: { user_id: user?.user_id, username: user?.username },
                        props: data,
                    });
                    return bulkGrantAffiliateAccess(data);
                },
                batchSize
            );
        },
        onSuccess: () => {
            invalidateQueries();
            stanNotify({
                type: 'success',
                title: 'Success',
                text: 'Product access has been granted.', // Match Vue text (affiliate uses same text)
                duration: 10000, // Match Vue duration
            });
            options.resetState();
            options.onSuccess?.();
        },
        onError: (error: Error) => {
            stanNotify({
                type: 'error',
                title: 'Product Access could not be granted, let us help you with that!', // Match Vue error title
                text: error.message || 'Please email friends@stanwith.me', // Match Vue error text
                duration: 4000, // Match Vue error duration
            });
            options.resetState();
            options.onError?.(error);
        },
    });
}

export function useAddToFlowMutation(options: BulkMutationOptions) {
    const invalidateQueries = useInvalidateQueries();
    const fetchUserFanIdsMutation = useFetchBulkUserFanIds({
        onError: options.onError,
    });

    return useMutation({
        mutationFn: async ({ flowId }: { flowId: number }) => {
            const userFanIds = await fetchUserFanIdsMutation.mutateAsync(options.getFilterArray());
            await addToEmailFlow(flowId, { user_fan_ids: userFanIds });
            return userFanIds.length;
        },
        onSuccess: count => {
            invalidateQueries();
            stanNotify({
                type: 'success',
                title: 'Success',
                text: `${count} customer${count !== 1 ? 's' : ''} added to flow.`,
                duration: 5000,
            });
            options.resetState();
            options.onSuccess?.();
        },
        onError: (error: Error) => {
            stanNotify({
                type: 'error',
                title: 'Could not add customers to flow',
                text: error.message || 'Please try again or contact support.',
                duration: 5000,
            });
            options.resetState();
            options.onError?.(error);
        },
    });
}

export function useDeleteCustomersMutation(options: BulkMutationOptions) {
    const invalidateQueries = useInvalidateQueries();
    const { user } = useUser();
    const fetchUserFanIdsMutation = useFetchBulkUserFanIds({
        onError: options.onError,
    });

    return useMutation({
        mutationFn: async () => {
            const userFanIds = await fetchUserFanIdsMutation.mutateAsync(options.getFilterArray());
            let totalDeleted = 0;

            // Use the existing processBatch but wrap the batch function to track deleted count
            // Use batch size of 1000 to match Vue's numberOfRowsToProcess
            await options.processBatch(
                userFanIds,
                async (batch: number[]) => {
                    const data = { user_fan_ids: batch };
                    const result = await bulkDeleteCustomers(data);
                    totalDeleted += result.processed || 0;
                    // Track analytics per batch (matching Vue behavior)
                    stanAnalytics('creator-customers-deleted', {
                        meta: { user_id: user?.user_id, username: user?.username },
                        props: data,
                    });
                    return result;
                },
                1000 // Match Vue's numberOfRowsToProcess
            );

            return {
                totalDeleted,
                totalCount: userFanIds.length,
            };
        },
        onSuccess: result => {
            invalidateQueries();
            options.resetState();
            // Don't show notification here - let the modal handle it based on result
            options.onSuccess?.(result);
        },
        onError: (error: Error) => {
            stanNotify({
                type: 'error',
                title: 'Customers could not be deleted',
                text: error.message || 'Please try again or contact support.',
                duration: 4000, // Match Vue's error duration
            });
            options.resetState();
            options.onError?.(error);
        },
    });
}
